import { useEffect, useState } from 'react'

import { Box, BoxList, Section, Text } from '@factorialco/deprecated-design-system'
import {
  ContractTemplatesFragment,
  CustomFieldsResourceFragment,
  TaxonomiesFragment,
} from 'generated/resources/graphql'
import { sortBy } from 'lodash'

import SortableBoxList from 'modules/core/components/SortableBoxList'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import { ModalStates } from 'modules/core/lib/questionModalStates'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import ContractField from 'modules/contracts_settings/components/ContractFields/ContractField'
import {
  getFieldsModalAction,
  getFieldsModalDescription,
  getFieldsModalTitle,
  getModalIlustration,
  getTaxonomyName,
  tagLabel,
} from 'modules/contracts_settings/components/ContractFields/Taxonomies/taxonomyTranslator'
import {
  ContractFieldsProps,
  countryPrefixes,
} from 'modules/contracts_settings/components/ContractFields/types'
import useCustomFieldHandler from 'modules/legal_entities/hooks/useCustomFieldHandler'
import useCustomFieldMutations from 'modules/legal_entities/hooks/useCustomFieldMutations'

import CustomField from './CustomField'
import FieldModal from './CustomField/FieldModal'

type Props = {
  legalEntity: LegalEntity
  templateFields?: ContractTemplatesFragment
  taxonomies?: TaxonomiesFragment[]
  contractTemplateId?: number
  resourceFields: CustomFieldsResourceFragment[]
  canCreateCustomFields: boolean
  ecctIds?: number[]
  hasCompensationsModule?: boolean
}

const ContractFields = ({
  legalEntity,
  templateFields,
  taxonomies,
  contractTemplateId,
  resourceFields,
  canCreateCustomFields,
  ecctIds,
  hasCompensationsModule,
}: Props) => {
  const { company } = useCurrentSession()
  const [modalType, setModalType] = useState<ModalStates>('none')
  const [resource, setResource] = useState<CustomFieldsResourceFragment[]>(resourceFields)
  const prefix = countryPrefixes[legalEntity.country] || ''
  const countryFields = () => {
    if (!templateFields) return {}
    return Object.fromEntries(
      Object.entries(templateFields).filter(([key]) => key.startsWith(prefix))
    )
  }
  const compensationsData: { compensations: Array<ContractFieldsProps> | undefined } = {
    compensations: taxonomies?.map((compensation) => ({
      id: compensation.id,
      archivedAt: compensation.archived ? new Date() : null,
      name: compensation.name,
      archivable: !ecctIds?.includes(compensation.id),
    })),
  }

  useEffect(() => {
    setResource(resourceFields)
  }, [resourceFields])

  const contractFields: { [key: string]: Array<ContractFieldsProps> | undefined } = {
    ...countryFields(),
    compensations: compensationsData.compensations,
  }
  const {
    field,
    setField,
    handleInputChange,
    handleMandatoryChange,
    handleTypeChange,
    handleAddOption,
    handleOptionAction,
  } = useCustomFieldHandler(undefined)

  const { handleCreateCustomField, handleChangePosition, loadingCreateCustomField } =
    useCustomFieldMutations({
      companyId: company.id,
      legalEntityId: legalEntity.id,
      field,
      setModalType,
    })

  const handleAddNewField = () => {
    setModalType('new')
    setField({ new: true, labelText: '', options: [], required: false })
  }

  const onPositionUpdate = (newSortedFields: CustomFieldsResourceFragment[]) => {
    newSortedFields.forEach((item, newIndex) => {
      const previousPosition = item.position
      if (previousPosition !== newIndex && item.field?.id) {
        setResource((prev) =>
          prev.map((prevItem) => {
            if (prevItem.field?.id === item.field?.id) {
              return {
                ...prevItem,
                position: newIndex,
              }
            }
            return prevItem
          })
        )
        handleChangePosition(item.id, newIndex)
      }
    })
  }

  const items = resource.filter((item) => !!item.field && !!item.field.id)
  const sortedFields = sortBy(items, (item) => item.position)

  const tagIcon = (key: string) => {
    return key === 'compensations' ? 'lock-locked' : 'pin'
  }

  const tagHover = (key: string) => {
    return key === 'compensations'
      ? i18n.t('company_settings.general.employment_data.contract_taxonomies.tags.hover.default')
      : i18n.t('company_settings.general.employment_data.contract_taxonomies.tags.hover.country', {
          country: prefix,
        })
  }

  const RenderedField = ({
    item,
    index,
  }: {
    item: CustomFieldsResourceFragment
    index: number
  }) => {
    if (!item.field) return null

    return (
      <CustomField
        key={index}
        customField={item.field}
        canCreateCustomFields={canCreateCustomFields}
        companyId={company.id}
        legalEntityId={legalEntity.id}
      />
    )
  }

  return (
    <Section
      illustration="contract-handshake"
      title={i18n.t('settings.legal_entities.legal_entity.contracts.contract_fields.title')}
      description={i18n.t(
        'settings.legal_entities.legal_entity.contracts.contract_fields.description'
      )}
      paddingX="s0"
      action={{
        label: i18n.t('settings.custom_fields.create'),
        onClick: () => handleAddNewField(),
      }}
    >
      <Box flexDirection="column" gap="s24">
        <BoxList>
          {Object.keys(contractFields)
            .filter((key) => (hasCompensationsModule ? key != 'compensations' : true))
            .map((key, index) => (
              <ContractField
                key={index}
                name={getTaxonomyName(key.replace(prefix, ''))}
                values={contractFields[key]}
                originalKey={key}
                contractsTemplateId={contractTemplateId || 0}
                tag={{ icon: tagIcon(key), label: tagLabel(key), hover: tagHover(key) }}
                legalEntityId={legalEntity.id}
              />
            ))}
        </BoxList>
        <Text.Normal size="300" weight="semibold" color="grey1000">
          {i18n.t('settings.legal_entities.legal_entity.contracts.contract_fields.custom_fields')}
        </Text.Normal>
        <SortableBoxList
          items={sortedFields}
          onSortEnd={onPositionUpdate}
          emptyMessage={i18n.t('settings.custom_fields.blank_slate.description')}
          itemAsKey={(item) => item.field?.id.toString() || ''}
          renderItem={(item, index) => <RenderedField item={item} index={index} />}
        />
      </Box>
      {modalType === 'new' && (
        <FieldModal
          modalTitle={getFieldsModalTitle(modalType)}
          modalDescription={getFieldsModalDescription(modalType)}
          modalIllustration={getModalIlustration(modalType)}
          modalButton={getFieldsModalAction(modalType)}
          onSubmit={() => handleCreateCustomField()}
          onClose={() => setModalType('none')}
          onInputChange={(value) => handleInputChange(value)}
          onMandatoryChange={(value) => handleMandatoryChange(value)}
          onTypeChange={(value) => handleTypeChange(value)}
          onAddOption={handleAddOption}
          onOptionAction={handleOptionAction}
          field={field}
          canSubmit={!!field?.fieldType}
          isSubmitting={loadingCreateCustomField}
        />
      )}
    </Section>
  )
}
export default ContractFields
